/**
 * Source-wins diffing against the links recorded by previous runs.
 *
 * Rules (see the plan's sync section):
 *  - hash unchanged + target still present   → no-op
 *  - hash changed                            → update (source wins on geometry)
 *  - link exists but target_id is gone       → recreate, and say so in the report
 *  - previously `unmatched`                  → retry the mapping every run; the
 *    PlaneMAX catalog grows, so an item that was unimportable last week may be
 *    importable today
 *  - link with no incoming object            → `source_deleted`; the PlaneMAX row
 *    is never touched, only flagged
 */

import type { ExistingLink, PlannedOp } from './types'

export function indexLinks(links: ExistingLink[]): Map<string, ExistingLink> {
  const map = new Map<string, ExistingLink>()
  for (const link of links) {
    map.set(linkKey(link.sourceObjectType, link.sourceObjectId), link)
  }
  return map
}

export function linkKey(type: 'building' | 'item', sourceObjectId: string): string {
  return `${type}:${sourceObjectId}`
}

export interface DiffOutcome {
  op: PlannedOp
  linkId: string | null
  /** Set when a previously imported target no longer exists. */
  targetMissing: boolean
}

/**
 * Classify one incoming source object.
 *
 * @param link            The link from a previous run, if any.
 * @param sourceHash      Hash of the incoming object.
 * @param targetExists    Whether the previously written PlaneMAX row is still there.
 *                        Pass `true` when the executor cannot cheaply check —
 *                        a failed update is caught and reported at write time.
 * @param mappable        Whether this object can be imported at all right now.
 */
export function classifyObject(params: {
  link: ExistingLink | undefined
  sourceHash: string
  targetExists: boolean
  mappable: boolean
}): DiffOutcome {
  const { link, sourceHash, targetExists, mappable } = params

  if (!mappable) {
    return { op: 'unmatched', linkId: link?.id ?? null, targetMissing: false }
  }

  if (!link) {
    return { op: 'create', linkId: null, targetMissing: false }
  }

  // A previously unmatched (or failed) object has no target yet — retrying the
  // mapping succeeded, so this is a create even though the link already exists.
  if (link.status === 'unmatched' || link.status === 'error' || !link.targetId) {
    return { op: 'create', linkId: link.id, targetMissing: false }
  }

  if (!targetExists) {
    return { op: 'create', linkId: link.id, targetMissing: true }
  }

  if (link.sourceHash !== sourceHash) {
    return { op: 'update', linkId: link.id, targetMissing: false }
  }

  return { op: 'noop', linkId: link.id, targetMissing: false }
}

/** Links whose source object is absent from the incoming scene. */
export function findSourceDeleted(
  links: ExistingLink[],
  seenKeys: Set<string>
): ExistingLink[] {
  return links.filter(
    (link) =>
      link.status !== 'source_deleted' &&
      !seenKeys.has(linkKey(link.sourceObjectType, link.sourceObjectId))
  )
}
